/**
 * The kind walk's body (Figma 00, 22:10): `WalkBody` is the two columns under the bar (the canvas ground with the
 * sheet over the readers' strip, and the leaves beside, 440 wide), `PageSheet` the white sheet with its soft
 * shadow, `SideHead` the leaves column's title and its note.
 */
import type { ReactNode } from 'react';
import { SPACE, TEXT, WALK, WEIGHT, usePalette } from '../theme';

export function WalkBody({ canvas, strip, side }: { canvas: ReactNode; strip: ReactNode; side: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: p.canvasGround }}>
        <div
          role="region"
          aria-label="Printed page"
          tabIndex={0}
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: `${SPACE.cozy}px ${SPACE.wide}px ${SPACE.snug}px`,
          }}
        >
          {canvas}
        </div>
        {strip}
      </div>
      <aside
        aria-label="Leaves"
        tabIndex={0}
        style={{
          width: WALK.side,
          flexShrink: 0,
          boxSizing: 'border-box',
          borderLeft: `1px solid ${p.hairline}`,
          background: p.surface,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {side}
      </aside>
    </div>
  );
}

export function PageSheet({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ width: WALK.sheet, maxWidth: '100%', flexShrink: 0, background: p.surface, boxShadow: `0 1px 1.5px ${p.shadow}` }}>{children}</div>
  );
}

export function SideHead({ title, note }: { title: ReactNode; note: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xxs, padding: `${SPACE.cozy}px ${SPACE.lg}px ${SPACE.snug}px` }}>
      <h2 style={{ margin: 0, fontSize: TEXT.body, fontWeight: WEIGHT.strong, color: p.ink, lineHeight: 'normal' }}>{title}</h2>
      <p style={{ margin: 0, fontSize: TEXT.caption, color: p.inkSecondary, lineHeight: 'normal' }}>{note}</p>
    </div>
  );
}

/** A padded block in the leaves column (the drafted fixes, the verdict that follows the list): its children stacked. */
export function SideBlock({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.tight, padding: `${SPACE.md}px ${SPACE.lg}px` }}>{children}</div>;
}
